/* wb-product-reviews-length.js — « Plus pertinents » des avis de fiche : les plus
   longs d'abord (demande de Simon, 01/10/2026).
   Copie de référence du dépôt parent : l'original déployé est
   `assets/wb-product-reviews-length.js` du thème live (en cas de doute le thème live
   fait foi). Chargé en `defer` par `sections/wb-product-reviews.liquid`, juste avant
   `assets/wb-product-reviews.js` qui s'en sert, et seulement sur une fiche de plus de
   cinq avis : en deçà, la section les a déjà tous triés.

   L'API de Judge.me ne trie pas par longueur : on lit tous les avis de la fiche, 30 par
   requête (son maximum), une seule fois par page vue, et on les trie ici. Mesuré le
   01/10 : 98,4 % des 11 946 fiches notées tiennent en une requête ; la plus grosse, la
   GS30 (462 avis), en demande 16, soit 104 Ko compressés.

   Longueur = caractères hors espaces du texte d'ORIGINE, pas de sa traduction : même
   ordre dans les 7 langues, et le même que la section, qui compte `review.body` du
   métachamp (mesuré identique au texte de `body_html` le 01/10). À égalité, le plus
   récent d'abord. Seuls les 4 et 5 étoiles sont en tête (voir `groupOf`).

   Sans ce fichier, `wb-product-reviews.js` retombe sur « Plus pertinents » de Judge.me. */
window.WBReviewsLength = (function () {
  'use strict';

  var API_PAGE_SIZE = 30; // le maximum de `reviews_for_widget`, mesuré le 01/10
  var MAX_API_PAGES = 20; // 600 avis : une borne, la plus grosse fiche en a 462
  var CONCURRENCY = 4;
  var RETRY_DELAY_MS = 200;

  /* Règle de Simon (01/10) : seuls les 4 et 5 étoiles sont rangés par longueur en
     tête ; un 1 ou 2 étoiles ne doit jamais passer devant. Ensuite 3, 2, puis 1
     étoile, chacune par longueur. Même règle que la section (`wb_group`). */
  var TOP_RATING = 4;
  var TOP_GROUP = 9;

  function lengthOf(text) {
    return String(text || '').replace(/\s+/g, '').length;
  }

  function groupOf(review) {
    var rating = Number(review.rating) || 0;
    return rating >= TOP_RATING ? TOP_GROUP : rating;
  }

  function byLength(a, b) {
    return b.group - a.group
      || b.length - a.length
      || String(b.review.created_at || '').localeCompare(String(a.review.created_at || ''))
      || String(a.review.uuid).localeCompare(String(b.review.uuid));
  }

  /* `task` sur chaque élément, `limit` à la fois ; les résultats dans l'ordre des
     éléments. Le premier échec fait échouer l'ensemble. */
  function mapLimit(items, limit, task) {
    return new Promise(function (resolve, reject) {
      var results = new Array(items.length);
      var next = 0;
      var done = 0;
      var failed = false;
      if (!items.length) {
        resolve(results);
        return;
      }
      function launch() {
        if (failed || next >= items.length) return;
        var index = next++;
        task(items[index]).then(function (value) {
          results[index] = value;
          done++;
          if (done === items.length) resolve(results);
          else launch();
        }, function (error) {
          failed = true;
          reject(error);
        });
      }
      for (var k = 0; k < Math.min(limit, items.length); k++) launch();
    });
  }

  /* Les pages lues en une seule liste : un avis publié entre deux lectures décale
     les pages, il n'est compté qu'une fois. */
  function merge(pages) {
    var seen = {};
    var reviews = [];
    var translations = {};
    pages.forEach(function (data) {
      (data.reviews || []).forEach(function (review) {
        if (!review || !review.uuid || seen[review.uuid]) return;
        seen[review.uuid] = true;
        reviews.push(review);
      });
      var pageTranslations = data.review_translations || {};
      Object.keys(pageTranslations).forEach(function (uuid) {
        translations[uuid] = pageTranslations[uuid];
      });
    });
    return { reviews: reviews, translations: translations };
  }

  /* Une page refusée est relue une fois : sur 16 pages (GS30), un seul refus
     passager de l'API ferait sinon perdre tout le tri. */
  function withRetry(task) {
    return function (item) {
      return task(item).then(null, function () {
        return new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAY_MS); })
          .then(function () { return task(item); });
      });
    };
  }

  /* Un chargeur : `fetchPageOnce(page)` lit une page de 30 avis, `toText` donne le
     texte d'un `body_html`. Lit tout une fois et garde le résultat ; un échec n'est
     pas gardé, l'appel suivant relit. */
  function createLoader(fetchPageOnce, toText) {
    var fetchApiPage = withRetry(fetchPageOnce);
    var pending = null;
    function sortAll(pages) {
      var all = merge(pages);
      var keyed = all.reviews.map(function (review) {
        return { review: review, group: groupOf(review), length: lengthOf(toText(review.body_html)) };
      });
      keyed.sort(byLength);
      return { reviews: keyed.map(function (k) { return k.review; }), translations: all.translations };
    }
    return function load() {
      if (pending) return pending;
      pending = fetchApiPage(1).then(function (first) {
        var total = Math.min(parseInt((first.pagination || {}).total_pages, 10) || 1, MAX_API_PAGES);
        var rest = [];
        for (var page = 2; page <= total; page++) rest.push(page);
        return mapLimit(rest, CONCURRENCY, fetchApiPage).then(function (pages) {
          return sortAll([first].concat(pages));
        });
      });
      pending.then(null, function () { pending = null; });
      return pending;
    };
  }

  /* La suite à afficher, au format d'une page de l'API : avis de la note demandée
     (`rating`), hors ceux déjà affichés (`shown`, uuid → true), `count` au plus ;
     `more` dit s'il en reste après. */
  function select(all, options) {
    var rating = options.rating ? Number(options.rating) : 0;
    var shown = options.shown || {};
    var pool = all.reviews.filter(function (review) {
      return (!rating || Number(review.rating) === rating) && !shown[review.uuid];
    });
    return {
      reviews: pool.slice(0, options.count),
      review_translations: all.translations,
      more: pool.length > options.count,
    };
  }

  return { API_PAGE_SIZE: API_PAGE_SIZE, createLoader: createLoader, select: select };
})();
